import { useEffect, useRef, useState } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import { useUser, useClerk, SignedIn, SignedOut, SignInButton, SignUpButton } from '@clerk/clerk-react'
import { SubscriptionDetailsButton } from '@clerk/clerk-react/experimental'
import { cn } from '@/lib/utils'
import { CreditCard, LogOut, LogIn, Crown, HelpCircle } from 'lucide-react'
import { mainNavItems, createNavItems, utilitiesNavItems, type NavItem } from '@/config/navigation'
import { BottomSheet, Skeleton, MobileProfileSkeleton, MobileNavItemSkeleton, AuthModal } from '@/components/ui'
import { useNavItemAccess } from '@/hooks'

interface MobileMenuProps {
  isOpen: boolean
  onClose: () => void
}

export function MobileMenu({ isOpen, onClose }: MobileMenuProps) {
  const location = useLocation()
  const prevPathRef = useRef(location.pathname)

  // Close menu on route change (but not on initial mount)
  useEffect(() => {
    if (prevPathRef.current !== location.pathname) {
      prevPathRef.current = location.pathname
      if (isOpen) onClose()
    }
  }, [location.pathname, isOpen, onClose])

  return (
    <BottomSheet isOpen={isOpen} onClose={onClose} title="Navigation">
      <div className="px-5 py-4 pb-6 space-y-6">
        {/* Main Nav */}
        <NavSection items={mainNavItems} />

        {/* Create */}
        <NavSection items={createNavItems} label="Create" />

        {/* Utilities */}
        <NavSection items={utilitiesNavItems} label="Utilities" />

        {/* Legal links */}
        <div className="flex flex-wrap gap-x-3 gap-y-1">
          <NavLink to="/terms" className="text-xs text-[#444] hover:text-[#888] transition-colors">Terms</NavLink>
          <NavLink to="/privacy" className="text-xs text-[#444] hover:text-[#888] transition-colors">Privacy</NavLink>
          <NavLink to="/acceptable-use" className="text-xs text-[#444] hover:text-[#888] transition-colors">Acceptable Use</NavLink>
        </div>

        {/* Profile Actions */}
        <ProfileActions />
      </div>
    </BottomSheet>
  )
}

function NavSection({ items, label }: { items: NavItem[]; label?: string }) {
  return (
    <div className="space-y-1.5">
      {label && (
        <div className="pb-2 text-xs font-medium text-[#555] uppercase tracking-wider">
          {label}
        </div>
      )}
      {items.map((item) => (
        <MobileNavLink key={item.href} item={item} />
      ))}
    </div>
  )
}

function MobileNavLink({ item }: { item: NavItem }) {
  const Icon = item.icon
  const [showAuthModal, setShowAuthModal] = useState(false)
  const {
    isLoading,
    showUpgradeGradient,
    showFeatureGate,
    showSkeletonCrown,
    crownColor,
    label,
    shouldShowAuthOnClick,
  } = useNavItemAccess(item)

  // Show skeleton for special button while loading
  if (item.isSpecial && isLoading) {
    return <MobileNavItemSkeleton />
  }

  // If not signed in and clicking upgrade button, show auth modal instead of navigating
  if (showUpgradeGradient && shouldShowAuthOnClick) {
    return (
      <>
        <button
          onClick={() => setShowAuthModal(true)}
          className="flex items-center gap-3 w-full px-4 py-3 rounded-xl text-sm font-medium transition-all duration-200 bg-gradient-to-r from-violet-600/60 via-violet-400/60 to-violet-600/60 border border-violet-400/50 text-white shadow-[0_0_12px_rgba(139,92,246,0.2)]"
        >
          <Icon className="size-5" />
          {label}
        </button>
        <AuthModal
          isOpen={showAuthModal}
          onClose={() => setShowAuthModal(false)}
          variant="feature"
        />
      </>
    )
  }

  if (showUpgradeGradient) {
    return (
      <NavLink
        to={item.href}
        end={item.href === '/'}
        className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all duration-200 bg-gradient-to-r from-violet-600/60 via-violet-400/60 to-violet-600/60 border border-violet-400/50 text-white shadow-[0_0_12px_rgba(139,92,246,0.2)]"
      >
        <Icon className="size-5" />
        {label}
      </NavLink>
    )
  }

  // For items requiring a plan - show auth modal if not signed in
  if (shouldShowAuthOnClick) {
    return (
      <>
        <button
          onClick={() => setShowAuthModal(true)}
          className="flex items-center gap-3 w-full px-4 py-3 rounded-xl text-sm font-medium text-left transition-all duration-200 text-[#666] border border-transparent active:bg-white/[0.04]"
        >
          <Icon className="size-5" />
          <span className="flex-1 text-left">{label}</span>
          {showSkeletonCrown && <Skeleton className="size-4 rounded" />}
          {showFeatureGate && (
            <Crown className={cn("size-4", crownColor)} />
          )}
        </button>
        <AuthModal
          isOpen={showAuthModal}
          onClose={() => setShowAuthModal(false)}
          variant="feature"
        />
      </>
    )
  }

  return (
    <NavLink
      to={item.href}
      end={item.href === '/'}
      className={({ isActive }) =>
        cn(
          'flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all duration-200',
          isActive 
            ? 'text-white bg-white/[0.05] border border-white/[0.1]' 
            : 'text-[#666] border border-transparent active:bg-white/[0.04]'
        )
      }
    >
      <Icon className="size-5" />
      <span className="flex-1">{label}</span>
      {showSkeletonCrown && <Skeleton className="size-4 rounded" />}
      {showFeatureGate && (
        <Crown className={cn("size-4", crownColor)} />
      )}
    </NavLink>
  )
}

function ProfileActions() {
  const { user, isLoaded } = useUser()
  const { signOut } = useClerk()

  // Show skeleton while loading
  if (!isLoaded) {
    return <MobileProfileSkeleton />
  }

  return (
    <>
      <SignedIn>
        <div className="space-y-1">
          <div className="pb-2 text-xs font-medium text-[#555] uppercase tracking-wider">
            Account
          </div>
          <div className="flex items-center gap-3 px-4 py-3">
            <img
              src={user?.imageUrl}
              alt={user?.fullName || 'Profile'}
              className="size-8 rounded-full"
            />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">
                {user?.fullName || user?.username}
              </p>
            </div>
          </div>
          {/* Subscription button with Clerk's subscription details popover */}
          <SubscriptionDetailsButton>
            <button className="flex items-center gap-3 w-full px-4 py-3 rounded-xl text-sm text-[#999] active:bg-white/[0.04] transition-colors">
              <CreditCard className="size-5" strokeWidth={1.5} />
              Subscription
            </button>
          </SubscriptionDetailsButton>
          <NavLink
            to="/support"
            className="flex items-center gap-3 w-full px-4 py-3 rounded-xl text-sm text-[#999] active:bg-white/[0.04] transition-colors"
          >
            <HelpCircle className="size-5" strokeWidth={1.5} />
            Support
          </NavLink>
          <MobileActionButton icon={LogOut} label="Sign out" onClick={() => signOut()} />
        </div>
      </SignedIn>
      <SignedOut>
        <div className="space-y-3">
          <div className="pb-1 text-xs font-medium text-[#555] uppercase tracking-wider">
            Get Started
          </div>
          <div className="flex flex-col gap-3">
            <SignUpButton mode="modal">
              <button className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-sm font-medium text-white bg-gradient-to-r from-violet-500 to-purple-500 active:from-violet-400 active:to-purple-400 transition-colors">
                Sign Up Free
              </button>
            </SignUpButton>
            <SignInButton mode="modal">
              <button className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-sm font-medium text-white bg-white/[0.05] border border-white/[0.1] active:bg-white/[0.08] transition-colors">
                <LogIn className="size-4" />
                Log In
              </button>
            </SignInButton>
          </div>
        </div>
      </SignedOut>
    </>
  )
}

function MobileActionButton({
  icon: Icon,
  label,
  onClick,
}: {
  icon: React.ElementType
  label: string
  onClick: () => void
}) {
  return (
    <button
      onClick={onClick}
      className="flex items-center gap-3 w-full px-4 py-3 rounded-xl text-sm text-[#999] active:bg-white/[0.04] transition-colors"
    >
      <Icon className="size-5" strokeWidth={1.5} />
      {label}
    </button>
  )
}
